import React, { useState, useEffect } from 'react';
import { Row, Col, Typography, Button, Space, message } from 'antd';
import { EditOutlined, AudioOutlined, FolderOpenOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import getPublicAssetUrl from '../utils/publicAsset';
import './Banners.css';
import './flag-icons.css';

const { Title, Paragraph } = Typography;

const FlagIcon = ({ flagClass, flagEmoji, className = '', size = 'normal' }) => {
  return (
    <span 
      className={`flag-icon ${flagClass} ${size} ${className}`}
      data-emoji={flagEmoji}
      title={flagEmoji}
      role="img"
      aria-label={flagEmoji}
    />
  );
};

const Banners = ({ isLoggedIn, showLogin, mode }) => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleStart = () => {
    if (isLoggedIn) {
      navigate('/editor');
    } else {
      message.info('请先登录以开始使用Notolang英语');
      showLogin();
    }
  };

  const images = [
    getPublicAssetUrl('/banners_06_1.png'),
    getPublicAssetUrl('/banners_06_4.png')
  ];

  const shareImageUrl = getPublicAssetUrl('/banners_06_1.png');

  // 自动切换图片
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 4000);

    return () => clearInterval(timer);
  }, [images.length]);

  return (
    <>
      <Helmet>
        <title>Notolang - 语言学习平台 | 听抄训练 影子跟读</title>
        <meta name="description" content="Notolang 是语言学习平台，提供听抄训练、影子跟读、专属语料库等功能，帮助您更高效地记住单词、掌握表达。" />
        <meta name="keywords" content="语言学习,听抄训练,影子跟读,单词记忆,语料库,AI学习,在线学习" />
        <meta property="og:title" content="Notolang - 语言学习平台" />
        <meta property="og:description" content="Notolang 是语言学习平台，通过听抄训练和影子跟读，帮助您更高效地记住单词、掌握地道表达。" />
        <meta property="og:image" content={shareImageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://notolang.com/product" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Notolang - 语言学习平台" />
        <meta name="twitter:description" content="Notolang 是语言学习平台，通过听抄训练和影子跟读，帮助您更高效地记住单词、掌握地道表达。" />
        <meta name="twitter:image" content={shareImageUrl} />
        <link rel="canonical" href="https://notolang.com/product" />
      </Helmet>
      
      <header className="banners-container" role="banner">
        <div className="banners-inner">
          {/* 文字内容区域 */}
          <section className="banners-text-section">
            {/* 当前支持语言展示 */}
            <div className="language-tag-wrapper">
              <span className="language-tag-text">当前仅支持英语</span>
              <FlagIcon flagClass="flag-us" flagEmoji="🇺🇸" size="normal" />
            </div>

            <Title level={1} className="banners-title">
              用听抄训练实现单词的重复记忆，
              <br />
              借助影子跟读掌握<span className="banners-title-highlight">地道表达</span>
            </Title>
            
            <Paragraph className="banners-desc">
              我们支持制作专属单词库、文本、音频、视频、YouTube等多样化语料，
              满足您的个性化学习需求
            </Paragraph>
            
            <Space size="large">
              <Button 
                type="primary" 
                size="large" 
                onClick={handleStart}
                className="banners-start-btn"
                aria-label="开始体验Notolang语言学习平台"
              >
                开始体验
              </Button>
            </Space>
          </section>
          
          {/* 图片展示区域 */}
          <section className="banners-image-section">
            <div className="banners-image-wrapper">
              <img 
                src={images[currentImageIndex]}
                alt={`Notolang英语学习平台界面展示 - 学习工具${currentImageIndex + 1}`}
                className="banners-image"
                loading="lazy"
                onLoad={() => console.log(`图片${currentImageIndex + 1}显示成功`)}
                onError={(e) => {
                  console.error(`图片${currentImageIndex + 1}加载失败`);
                  e.target.style.background = '#f0f0f0';
                  e.target.style.minHeight = '200px';
                }}
              />
            </div>
            
            {/* 指示器 */}
            <nav className="banners-nav" aria-label="图片切换导航">
              {images.map((_, index) => (
                <button
                  key={index}
                  className={`banners-nav-dot ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(index)}
                  aria-label={`切换到第${index + 1}张图片`}
                  aria-current={index === currentImageIndex ? 'true' : 'false'}
                />
              ))}
            </nav>
          </section>

          {/* 特色功能简介 */}
          <section className="banners-features">
            <div className="banner-feature-card">
              <div className="banner-feature-icon"><EditOutlined /></div>
              <h3 className="banner-feature-title">听抄训练</h3>
              <p className="banner-feature-desc">
                通过反复听写练习，强化单词记忆，提升听力理解能力
              </p>
            </div>
            <div className="banner-feature-card">
              <div className="banner-feature-icon"><AudioOutlined /></div>
              <h3 className="banner-feature-title">影子跟读</h3>
              <p className="banner-feature-desc">
                模仿母语者发音，掌握地道表达，提升口语流利度
              </p>
            </div>
            <div className="banner-feature-card">
              <div className="banner-feature-icon"><FolderOpenOutlined /></div>
              <h3 className="banner-feature-title">多样语料</h3>
              <p className="banner-feature-desc">
                支持文本、音频、视频、YouTube等多种格式，满足不同学习需求
              </p>
            </div>
          </section>
        </div>
      </header>
    </>
  );
};

export default Banners;
